import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Flex,
  Table,
  Tooltip,
  Row,
  Col,
  Checkbox,
  message,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CustomParagraph,
  CustomPagination,
  TeamBadge,
  CommonDrawer,
} from '../../../components';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';

import {
  ArrowOutwardIcon,
  CallFillIcon,
  MailFillIcon,
  RoadmapFillIcon,
} from '@/assets/icons/icons';
import { ChevronRightIcon } from 'lucide-react';
import Pill from '@/pages/Shipments/AiTranscripts/ui/Pill';
import { SentimentLabel } from './SentimentLabel';
import { AIHistoryRecord, AIHistoryTableProps } from '../type';
import { isInboundCall } from '../utils/callDirection';
import { rowTimestamp, timestampHeader } from '../utils/timestampLabel';
import AiEmailExchange from '@/pages/Shipments/AiTranscripts/AIEmailExchange';
import AiCallTranscript from '@/pages/Shipments/AiTranscripts/AiTranscript';
import SettingsAgentDrawerForm, {
  AgentDrawerFormRef,
} from '@/pages/Settings/AiAgents/SettingsAgentDrawerForm';
import { AgentRow } from '@/pages/Settings/AiAgents/types';
import '@/pages/Shipments/AiTranscripts/Transcripts.css';
import { useToken } from '@/common/helper';
import useUnauthorize from '@/hooks/useUnauthorize';
import {
  oneTimeDismiss,
  oneTimeResolve,
  ResolveAlertComments,
  ResolveAlertCommentsModel,
} from '@/apis/shipment';
import { useSpin } from '@/components/SpinContext';
import ShipmentDrawer from '@/pages/Shipments/ShipmentDrawer';

type DrawerState = {
  isOpen: boolean;
  name: string;
  shipmentId: string | null;
  alert: unknown | null;
  legType?: 'CL' | 'EL';
};

type ToggleDrawerFn = (
  state: 'open' | 'close',
  name: string,
  shipmentId: string | null,
  alert?: unknown,
  legType?: 'CL' | 'EL'
) => void;

// Full "Sep 28 2026, 12:15 PM" + 8px gap + 40px type icons + 12px padding.
const TIMESTAMP_COLUMN_WIDTH = 200;
// Fits common values ("Out for delivery"); longer ones ellipsize with a tooltip.
const SHIP_STATUS_COLUMN_WIDTH = 100;
const SCROLL_MODE_MAX_WIDTH = 1280;

const AIHistoryTable: React.FC<AIHistoryTableProps> = ({
  onRefresh,
  historyList,
  page,
  setPage,
  totalPages,
  timezoneAbbr,
  checked,
  selectedIds,
  excludedIds,
  selectAllAcrossFilters,
  handleSelect,
}) => {
  const { t } = useTranslation('aiHistory');
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const { unauthorize } = useUnauthorize();
  const location = useLocation();
  const shipmentDrawerWrapRef = useRef<HTMLDivElement>(null);
  const isRowSelected = (recordId: string) =>
    selectAllAcrossFilters
      ? !excludedIds.includes(recordId)
      : selectedIds.includes(recordId);
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [agentPanel, setAgentPanel] = useState<{
    open: boolean;
    agentId: string | null;
    agentType: string | null;
    readOnly: boolean;
  }>({ open: false, agentId: null, agentType: null, readOnly: true });
  const formRef = useRef<AgentDrawerFormRef>(null);

  const openAgent = (agentId: string, readOnly: boolean): void => {
    setAgentPanel({ open: true, agentId, agentType: null, readOnly });
  };

  const closeAgent = (): void => {
    setAgentPanel({
      open: false,
      agentId: null,
      agentType: null,
      readOnly: true,
    });
  };

  const [selectedData, setSelectedData] = useState<AIHistoryRecord>(
    {} as AIHistoryRecord
  );

  const [drawerState, setDrawerState] = useState<DrawerState>({
    isOpen: false,
    name: '',
    shipmentId: null,
    alert: null,
  });

  const [isWideScreen, setIsWideScreen] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth >= 1440 : false
  );

  const [isScrollMode, setIsScrollMode] = useState<boolean>(
    typeof window !== 'undefined'
      ? window.innerWidth < SCROLL_MODE_MAX_WIDTH
      : false
  );

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setIsWideScreen(w >= 1440);
      setIsScrollMode(w < SCROLL_MODE_MAX_WIDTH);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (location?.state?.openAddModal) {
      setShowDrawer(true);
    }
  }, [location?.state]);

  const toggleDrawer: ToggleDrawerFn = useCallback(
    (state, name, shipmentId, alert, legType) => {
      const isOpening = state === 'open';
      setDrawerState({
        isOpen: isOpening,
        name,
        shipmentId: isOpening ? shipmentId : null,
        alert: alert ?? null,
        legType: isOpening ? legType : undefined,
      });
    },
    []
  );

  const openShipmentDrawerFromRef = (
    shipmentId?: string | number | null,
    legType?: 'CL' | 'EL'
  ) => {
    if (!shipmentId) return;
    toggleDrawer('open', 'Detail', String(shipmentId), null, legType);
  };

  const handlePageChange = (newPage: number) => {
    const validPage = newPage > 0 ? newPage : 1;
    setPage(validPage);
  };

  const [alertId, setAlertId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const onClose = (close?: boolean): void => {
    if (close) setShowDrawer(false);
  };

  interface RawAlert {
    eventId?: string;
    alert_id?: string;
    id?: string;
    notification_type?: string;
    ai_events?: Array<{ id?: string }>;
  }

  const normalizeAlertForTranscript = useCallback((raw?: RawAlert) => {
    if (!raw) return undefined;

    const ai_events = (raw.ai_events ?? [])
      .map((e) => ({ id: String(e?.id ?? '').trim() }))
      .filter((e) => e.id.length > 0);

    return {
      ...raw,
      eventId: raw.eventId,
      id: raw.id,
      ai_events: ai_events.length ? ai_events : undefined,
    };
  }, []);

  const normalizedAlert = React.useMemo(
    () =>
      normalizeAlertForTranscript(selectedData?.alert as RawAlert | undefined),
    [normalizeAlertForTranscript, selectedData?.alert]
  );

  const buildResolveModel = (
    isResolved: boolean
  ): ResolveAlertCommentsModel => ({
    is_resolved: isResolved,
    pk_list: [alertId],
  });

  const getOneTimeId = (): string | undefined => {
    const a = selectedData?.alert as RawAlert | undefined;
    return a?.eventId ?? a?.id;
  };

  const runAlertAction = async (isResolve: boolean) => {
    showSpin();
    try {
      const a = selectedData?.alert as RawAlert | undefined;
      const isOneTime = a?.notification_type === 'one_time_notification';

      if (isOneTime) {
        const id = getOneTimeId();
        const res = await (isResolve
          ? oneTimeResolve(token, id)
          : oneTimeDismiss(token, id));

        const data = res?.data?.data || null;
        if (data?.is_success) {
          setRefreshKey((prev) => prev + 1);
          onRefresh?.();
          message.success(
            data?.message ||
              (isResolve ? 'Resolved successfully' : 'Dismissed successfully')
          );
        } else {
          message.error(
            data?.message || (isResolve ? 'Resolve failed' : 'Dismiss failed')
          );
        }
      } else {
        const model = buildResolveModel(isResolve);
        const res = await ResolveAlertComments(token, model);
        const data = res?.data || null;

        if (data?.data?.is_success) {
          onRefresh?.();
          setRefreshKey((prev) => prev + 1);
          message.success(
            data?.data?.message ||
              (isResolve ? 'Resolved successfully' : 'Dismissed successfully')
          );
        } else {
          message.error(
            data?.data?.message ||
              (isResolve ? 'Resolve failed' : 'Dismiss failed')
          );
        }
      }
    } catch {
      unauthorize();
    } finally {
      hideSpin();
    }
  };

  const handleDismiss = (): void => void runAlertAction(false);
  const handleResolve = (): void => void runAlertAction(true);

  const handleSelectedRowData = (data: AIHistoryRecord) => {
    setSelectedData(data);
    closeAgent();
    setShowDrawer(true);
  };

  const columns: ColumnsType<AIHistoryRecord> = [
    ...(checked
      ? [
          {
            title: '',
            dataIndex: 'select',
            key: 'select',
            width: 20,
            render: (_, record) => (
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                className='for-reports-filters-selection'
              >
                <Checkbox
                  checked={isRowSelected(record.id)}
                  onChange={(e) =>
                    handleSelect?.(record.id, e.target.checked, record)
                  }
                />
              </div>
            ),
          } as ColumnsType<AIHistoryRecord>[number],
        ]
      : []),

    {
      title: timestampHeader(timezoneAbbr),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: TIMESTAMP_COLUMN_WIDTH,
      render: (_, record) => (
        <Flex
          gap={8}
          align='center'
          justify='space-between'
          style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}
        >
          <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
            {/* Fits a normal value; a DST suffix or extra icons ellipsize. */}
            <Typography.Text
              ellipsis={{ tooltip: rowTimestamp(record, timezoneAbbr) }}
              style={{ width: '100%', fontWeight: 'inherit' }}
            >
              {rowTimestamp(record, timezoneAbbr)}
            </Typography.Text>
          </div>

          <Flex
            gap={8}
            align='center'
            style={{ marginBottom: 0, flexShrink: 0 }}
          >
            {record?.action_type == 'email' ? (
              <Tooltip title={t('emailTooltip')}>
                <span style={{ display: 'inline-flex' }}>
                  <MailFillIcon />
                </span>
              </Tooltip>
            ) : (
              <Tooltip
                title={t(
                  isInboundCall(record)
                    ? 'inboundCallTooltip'
                    : 'outboundCallTooltip'
                )}
              >
                <span style={{ display: 'inline-flex' }}>
                  <CallFillIcon />
                </span>
              </Tooltip>
            )}

            <span style={{ display: 'inline-flex', width: 16 }}>
              {record?.is_escalated && (
                <Tooltip title={t('openEscalationTranscript')}>
                  <span
                    className='escalation-arrow'
                    style={{ display: 'inline-flex', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectedRowData({
                        ...record,
                        alert: record.escalated_alert ?? record.alert,
                        action_type: 'call',
                      });
                    }}
                  >
                    <ArrowOutwardIcon />
                  </span>
                </Tooltip>
              )}
            </span>

            {record?.workFlow && (
              <Tooltip
                title={
                  'Completed as part of an automation (future feature: no code workflow)'
                }
              >
                <span style={{ display: 'inline-flex' }}>
                  <RoadmapFillIcon />
                </span>
              </Tooltip>
            )}
          </Flex>
        </Flex>
      ),
    },

    {
      title: 'AI state',
      dataIndex: 'status_info',
      key: 'status_info',
      width: isScrollMode ? 140 : 124,
      render: (_, record) => (
        <Flex align='center' gap={8}>
          <Pill style={{ whiteSpace: 'nowrap', maxWidth: '100%' }}>
            {String(record?.status_info?.description || '')}
          </Pill>
        </Flex>
      ),
    },

    {
      title: 'Type',
      dataIndex: 'alert_title',
      key: 'alert_title',
      width: isWideScreen ? 170 : 130,
      render: (text) => (
        <Flex align='center' style={{ whiteSpace: 'nowrap', width: '100%' }}>
          <Typography.Text
            ellipsis={{ tooltip: text }}
            style={{
              width: '100%',
            }}
          >
            {text}
          </Typography.Text>
        </Flex>
      ),
    },

    {
      title: 'Sentiment',
      dataIndex: 'sentiment',
      key: 'sentiment',
      width: 86,
      render: (text) =>
        typeof text === 'string' ? (
          <SentimentLabel sentimentLabel={text} />
        ) : (
          'N/A'
        ),
    },

    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 76,
      render: (text) => (
        <CustomParagraph
          text={String(text || '--')}
          fontSize='14px'
          fontWeight={400}
          color='var(--text-primary)'
        />
      ),
    },

    {
      title: 'From / To',
      dataIndex: 'From / To',
      key: 'from/to',
      // Gives up width to the untruncated Timestamp in the mid-size layout;
      // each side already ellipsizes with a tooltip.
      width: isScrollMode ? 230 : isWideScreen ? 180 : 102,
      onCell: () => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        },
      }),
      // Sender keeps its width up to half the cell, recipient takes the rest;
      // either ellipsizes with a tooltip. With no recipient, the sender gets it all.
      render: (_, record) => (
        <Flex align='center' gap={4} style={{ minWidth: 0 }}>
          <Typography.Text
            ellipsis={{ tooltip: record?.action_from }}
            style={
              record?.action_to
                ? { flex: '0 0 auto', maxWidth: '50%' }
                : { flex: '0 1 auto', minWidth: 0 }
            }
          >
            {record?.action_from}
          </Typography.Text>
          <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>➜</span>
          {record?.action_to && (
            <Typography.Text
              ellipsis={{ tooltip: record.action_to }}
              style={{ flex: '0 1 auto', minWidth: 0 }}
            >
              {record.action_to}
            </Typography.Text>
          )}
        </Flex>
      ),
    },

    {
      title: themeT('referenceId', theme.themeType),
      dataIndex: 'reference_id',
      key: 'reference_id',
      width: isScrollMode ? 140 : 120,

      onCell: () => ({
        style: {
          whiteSpace: 'nowrap',
        },
      }),
      render: (text, record) => {
        const shipmentId = record?.shipment_id;

        return (
          <div
            data-open-shipment-drawer='true'
            role='button'
            tabIndex={shipmentId ? 0 : -1}
            style={{
              display: 'inline-block',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              cursor: shipmentId ? 'pointer' : 'default',
              width: '100%',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (shipmentId)
                openShipmentDrawerFromRef(shipmentId, record?.leg_type ?? 'CL');
            }}
            onKeyDown={(e) => {
              if (!shipmentId) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                openShipmentDrawerFromRef(shipmentId, record?.leg_type ?? 'CL');
              }
            }}
          >
            <Typography.Text
              ellipsis={{ tooltip: text }}
              style={{
                width: '100%',
                ...(text && {
                  textDecoration: 'underline dotted',
                  textUnderlineOffset: '3px',
                }),
              }}
            >
              {text || '--'}
            </Typography.Text>
          </div>
        );
      },
    },

    {
      title: 'Ship. Status',
      dataIndex: 'ship_progress',
      key: 'ship_progress',
      width: SHIP_STATUS_COLUMN_WIDTH,
      render: (text) => (
        <Typography.Text
          ellipsis={{ tooltip: text }}
          style={{
            width: '100%',
            fontSize: 14,
            fontWeight: 400,
            color: 'var(--text-primary)',
          }}
        >
          {String(text || '--')}
        </Typography.Text>
      ),
    },

    {
      title: `${theme.themeType === 2 ? 'Clients' : 'Teams'}`,
      dataIndex: 'teams',
      key: 'teams',
      width: 150,
      ellipsis: true,
      onCell: () => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        },
      }),
      render: (_, record) => (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            maxWidth: '100%',
          }}
        >
          {record?.teams?.length > 0 ? (
            <>
              <TeamBadge
                truncateLength={isScrollMode ? 40 : 10}
                color={record?.teams[0]?.color}
                text={record?.teams[0]?.name}
                tooltipText={record?.teams[0]?.name}
              />

              {record?.teams.length > 1 && (
                <Tooltip
                  title={record.teams
                    .slice(1)
                    .map((team) => team.name)
                    .join(', ')}
                  placement='top'
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    <TeamBadge
                      color={null}
                      text={`+ ${record.teams.length - 1}`}
                    />
                  </span>
                </Tooltip>
              )}
            </>
          ) : (
            <span>--</span>
          )}
        </div>
      ),
    },

    {
      title: '',
      key: 'action',
      width: 40,
      render: (_, record) => (
        <Tooltip
          title={t(
            record?.action_type === 'email'
              ? 'openEmailThread'
              : 'openCallTranscript'
          )}
        >
          <Flex>
            <ChevronRightIcon width={16} height={16} />
          </Flex>
        </Tooltip>
      ),
    },
  ];

  const shipmentSuccess = () => {};
  const filterByLinkCode = (_: string | null) => {};
  const onEditShipmentSuccess = (_: unknown) => {};
  const onLinkedShipmentClick = (_: string) => {};

  return (
    <>
      <div className='settings-content-wrapper'>
        <Table
          columns={columns}
          dataSource={historyList.map((r) => ({ ...r, key: r.id }))}
          pagination={false}
          className='settings-table ai-history-table'
          tableLayout='fixed'
          size='middle'
          scroll={isScrollMode ? { x: 'max-content' } : undefined}
          onRow={(record) => ({
            onClick: () => handleSelectedRowData(record),
            style: { cursor: 'pointer' },
          })}
        />

        {historyList.length > 0 && (
          <Row style={{ width: '100%', marginTop: '12px' }} justify='center'>
            <Col>
              <CustomPagination
                page={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </Col>
          </Row>
        )}
      </div>

      <CommonDrawer
        width={agentPanel.open ? 444 : 552}
        open={showDrawer}
        onClose={() => {
          setShowDrawer(false);
          closeAgent();
        }}
        onBack={agentPanel.open ? closeAgent : undefined}
      >
        <>
          {agentPanel.open && agentPanel.agentId ? (
            <SettingsAgentDrawerForm
              ref={formRef}
              agentData={{ id: agentPanel.agentId } as AgentRow}
              drawerOpen
              onClose={() => {
                setShowDrawer(false);
                closeAgent();
              }}
              readOnly={agentPanel.readOnly}
              onAgentTypeLoaded={(type) =>
                setAgentPanel((prev) => ({ ...prev, agentType: type }))
              }
            />
          ) : (
            <>
              {selectedData?.action_type === 'email' && (
                <AiEmailExchange
                  key={refreshKey}
                  setAlertId={setAlertId}
                  onClose={onClose}
                  unauthorize={unauthorize}
                  token={token}
                  alert={normalizedAlert}
                  onDismiss={() => handleDismiss()}
                  onResolve={() => handleResolve()}
                  onOpenAgent={openAgent}
                  shipmentId={selectedData?.shipment_id}
                  showHeaderButtons={false}
                />
              )}

              {selectedData?.action_type === 'call' && (
                <AiCallTranscript
                  key={refreshKey}
                  setAlertId={setAlertId}
                  onClose={onClose}
                  unauthorize={unauthorize}
                  token={token}
                  alert={normalizedAlert}
                  onDismiss={() => handleDismiss()}
                  onResolve={() => handleResolve()}
                  onOpenAgent={openAgent}
                  shipmentId={selectedData?.shipment_id}
                  showHeaderButtons={false}
                />
              )}
            </>
          )}
        </>
      </CommonDrawer>

      <div ref={shipmentDrawerWrapRef}>
        <ShipmentDrawer
          fromTable={true}
          onLinkedShipmentClick={onLinkedShipmentClick}
          allPermissions={null}
          drawerState={drawerState}
          legType={drawerState.legType}
          toggleDrawer={toggleDrawer}
          shipmentSuccess={shipmentSuccess}
          filterByLinkCode={filterByLinkCode}
          onEditShipmentSuccess={onEditShipmentSuccess}
        />
      </div>
    </>
  );
};

export default AIHistoryTable;
